import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  LayoutDashboard, Flag, Users, Briefcase, Award, BarChart3,
  MessageSquare, ImageIcon, ClipboardCheck, Menu, X, ChevronDown,
  Mail, BookOpen, Wallet, ThumbsUp, ShieldCheck, ChevronsLeft, ChevronsRight
} from 'lucide-react';

const STORAGE_KEY = 'admin_sidebar_collapsed';

const NavigationTabs = ({
  activeTab,
  setActiveTab,
  reports,
  users,
  jobs,
  pendingFeedbackCount = 0,
  pendingPictureReviews = 0,
  pendingJobModerations = 0,
  pendingChatsCount = 0,
  pendingSkillModerations = 0,
}) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(STORAGE_KEY) === 'true'
  );
  // Track which sections are explicitly collapsed by the user (all open by default)
  const [closedSections, setClosedSections] = useState(new Set());

  const tabs = useMemo(() => [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: LayoutDashboard,
      count: 0,
      link: '/admin-moderation?tab=dashboard',
      section: 'main',
    },
    {
      id: 'reports',
      label: 'Reports',
      icon: Flag,
      count: reports.filter(r => r.status === 'submitted').length,
      link: '/admin-moderation?tab=reports',
      section: 'moderation',
    },
    {
      id: 'users',
      label: 'Users',
      icon: Users,
      count: users.filter(u => u.moderation_status?.status === 'suspended').length +
             users.filter(u => u.identity_verification_status === 'pending').length,
      link: '/admin-moderation?tab=users',
      section: 'moderation',
    },
    {
      id: 'jobs',
      label: 'Jobs',
      icon: Briefcase,
      count: jobs.filter(j => j.status === 'flagged').length,
      link: '/admin-moderation?tab=jobs',
      section: 'moderation',
    },
    {
      id: 'job-moderation',
      label: 'Job Moderation',
      icon: ClipboardCheck,
      count: pendingJobModerations,
      link: '/admin-moderation?tab=job-moderation',
      section: 'moderation',
    },
    {
      id: 'profile-pictures',
      label: 'Profile Pictures',
      icon: ImageIcon,
      count: pendingPictureReviews,
      link: '/admin-moderation?tab=profile-pictures',
      section: 'moderation',
    },
    {
      id: 'user-skills',
      label: 'User Skills',
      icon: Award,
      count: pendingSkillModerations,
      link: '/admin-moderation?tab=user-skills',
      section: 'moderation',
    },
    {
      id: 'ai-credits',
      label: 'AI Credits',
      icon: Wallet,
      count: 0,
      link: '/admin-moderation?tab=ai-credits',
      section: 'moderation',
    },
    {
      id: 'feedback',
      label: 'Feedback',
      icon: ThumbsUp,
      count: pendingFeedbackCount,
      link: '/admin-moderation?tab=feedback',
      section: 'support',
    },
    {
      id: 'live-chat',
      label: 'Live Chat',
      icon: MessageSquare,
      count: pendingChatsCount,
      link: '/admin-moderation?tab=support',
      section: 'support',
    },
    {
      id: 'help-cms',
      label: 'Help Articles',
      icon: BookOpen,
      count: 0,
      link: '/admin-moderation?tab=help-cms',
      section: 'support',
    },
    {
      id: 'marketing-campaigns',
      label: 'Campaigns',
      icon: Mail,
      count: 0,
      link: '/admin-moderation?tab=marketing-campaigns',
      section: 'marketing',
    },
    {
      id: 'analytics',
      label: 'Analytics',
      icon: BarChart3,
      count: 0,
      link: '/admin-moderation?tab=analytics',
      section: 'insights',
    },
  ], [reports, users, jobs, pendingJobModerations, pendingPictureReviews, pendingFeedbackCount, pendingChatsCount, pendingSkillModerations]);

  // Auto-open the section that contains the active tab when tab changes
  useEffect(() => {
    const activeTabDef = tabs.find(t => t.id === activeTab);
    if (activeTabDef && activeTabDef.section !== 'main') {
      setClosedSections(prev => {
        const next = new Set(prev);
        next.delete(activeTabDef.section);
        return next;
      });
    }
  }, [activeTab, tabs]);

  const toggleCollapse = useCallback(() => {
    setCollapsed(prev => {
      const next = !prev;
      localStorage.setItem(STORAGE_KEY, String(next));
      return next;
    });
  }, []);

  const toggleSection = useCallback((section) => {
    setClosedSections(prev => {
      const next = new Set(prev);
      if (next.has(section)) {
        next.delete(section);
      } else {
        next.add(section);
      }
      return next;
    });
  }, []);

  const sections = useMemo(() => ({
    main: { label: '', color: '', items: tabs.filter(t => t.section === 'main') },
    moderation: { label: 'Moderation', color: 'text-orange-500', items: tabs.filter(t => t.section === 'moderation') },
    support: { label: 'Support', color: 'text-green-500', items: tabs.filter(t => t.section === 'support') },
    marketing: { label: 'Marketing', color: 'text-purple-500', items: tabs.filter(t => t.section === 'marketing') },
    insights: { label: 'Insights', color: 'text-blue-500', items: tabs.filter(t => t.section === 'insights') },
  }), [tabs]);

  const sidebarWidth = collapsed ? 'w-16' : 'w-64';

  const NavItem = ({ tab }) => {
    const isActive = activeTab === tab.id;
    return (
      <Link
        to={tab.link}
        title={collapsed ? tab.label : undefined}
        onClick={() => {
          setActiveTab(tab.id);
          if (window.innerWidth < 1024) setMobileOpen(false);
        }}
        className={`group relative flex items-center ${collapsed ? 'justify-center px-0' : 'justify-between px-3'} py-2.5 rounded-lg text-sm font-medium transition-all duration-150 ${
          isActive
            ? 'bg-blue-50 text-blue-700'
            : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
        }`}
      >
        {/* Active indicator stripe */}
        {isActive && (
          <span className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 bg-blue-600 rounded-r-full" />
        )}

        <div className={`flex items-center ${collapsed ? '' : 'space-x-3'}`}>
          <tab.icon
            size={18}
            className={`flex-shrink-0 ${isActive ? 'text-blue-600' : 'text-gray-400 group-hover:text-gray-600'}`}
          />
          {!collapsed && <span className="truncate">{tab.label}</span>}
        </div>

        {/* Badge */}
        {tab.count > 0 && !collapsed && (
          <span className="ml-auto flex-shrink-0 bg-red-100 text-red-600 text-xs font-semibold px-1.5 py-0.5 rounded-full min-w-[20px] text-center">
            {tab.count > 99 ? '99+' : tab.count}
          </span>
        )}
        {/* Collapsed badge dot */}
        {tab.count > 0 && collapsed && (
          <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full" />
        )}

        {/* Tooltip for collapsed mode */}
        {collapsed && (
          <span className="pointer-events-none absolute left-full ml-2 whitespace-nowrap rounded-md bg-gray-900 px-2 py-1 text-xs text-white opacity-0 group-hover:opacity-100 transition-opacity z-50 shadow-lg">
            {tab.label}
            {tab.count > 0 && <span className="ml-1 text-red-300">({tab.count})</span>}
          </span>
        )}
      </Link>
    );
  };

  const SidebarContent = () => (
    <nav className="flex flex-col h-full">
      {/* Branding / Collapse toggle */}
      <div className={`flex items-center ${collapsed ? 'justify-center px-2' : 'justify-between px-4'} py-4 border-b border-gray-100`}>
        {!collapsed && (
          <div className="flex items-center space-x-2">
            <div className="w-7 h-7 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
              <ShieldCheck size={14} className="text-white" />
            </div>
            <span className="text-sm font-bold text-gray-800 tracking-tight">Admin Panel</span>
          </div>
        )}
        <button
          onClick={toggleCollapse}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className="p-1.5 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors flex-shrink-0"
        >
          {collapsed ? <ChevronsRight size={16} /> : <ChevronsLeft size={16} />}
        </button>
      </div>

      {/* Scrollable nav items */}
      <div className="flex-1 overflow-y-auto py-3 space-y-1 px-2">
        {Object.entries(sections).map(([sectionKey, section]) => {
          const isClosed = closedSections.has(sectionKey);
          const sectionTotal = section.items.reduce((sum, t) => sum + t.count, 0);

          return (
            <div key={sectionKey} className={section.label ? 'mb-1' : 'mb-3'}>
              {/* Section header */}
              {section.label && !collapsed && (
                <button
                  onClick={() => toggleSection(sectionKey)}
                  className="w-full flex items-center justify-between px-2 py-1.5 mb-1 rounded-md hover:bg-gray-50 transition-colors group"
                >
                  <span className={`text-[10px] font-bold uppercase tracking-widest ${section.color}`}>
                    {section.label}
                  </span>
                  <div className="flex items-center space-x-1">
                    {sectionTotal > 0 && isClosed && (
                      <span className="text-xs font-semibold text-red-500">{sectionTotal}</span>
                    )}
                    <ChevronDown
                      size={13}
                      className={`text-gray-400 transition-transform ${isClosed ? '-rotate-90' : ''}`}
                    />
                  </div>
                </button>
              )}

              {/* Section divider in collapsed mode */}
              {section.label && collapsed && (
                <div className="my-2 mx-3 border-t border-gray-100" />
              )}

              {/* Items */}
              {(!section.label || !isClosed || collapsed) && (
                <div className="space-y-0.5">
                  {section.items.map(tab => (
                    <NavItem key={tab.id} tab={tab} />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </nav>
  );

  return (
    <>
      {/* Mobile top bar — fixed overlay, doesn't affect document flow */}
      <div className="lg:hidden fixed top-0 left-0 right-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between z-50 shadow-sm">
        <div className="flex items-center space-x-2">
          <div className="w-6 h-6 bg-blue-600 rounded-md flex items-center justify-center">
            <ShieldCheck size={12} className="text-white" />
          </div>
          <span className="font-semibold text-gray-800 text-sm">Admin Panel</span>
        </div>
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="p-1.5 hover:bg-gray-100 rounded-md text-gray-600 transition-colors"
          aria-label="Toggle navigation"
        >
          {mobileOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {/* Desktop sidebar */}
      <aside
        className={`hidden lg:flex flex-col flex-shrink-0 ${sidebarWidth} h-screen sticky top-0 bg-white border-r border-gray-200 overflow-hidden transition-all duration-200 ease-in-out`}
      >
        <SidebarContent />
      </aside>

      {/* Mobile sidebar drawer */}
      <aside
        className={`lg:hidden fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 transform transition-transform duration-300 ease-in-out ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <SidebarContent />
      </aside>

      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/40 z-40"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}
    </>
  );
};

export default NavigationTabs;
